import { useCallback } from 'react';

import { TypedUseQueryHookResult } from '@reduxjs/toolkit/dist/query/react';

import { AxiosBaseQueryFunction } from '~/api/axiosBaseQuery';
import {
  RetailerFileSettingsParams,
  useFetchRetailerFileSettingsQuery,
} from '~/api/easy-estimator-api/retailer-settings-api/retailerSettingsFileService';
import {
  RetailerSettingsParams,
  useFetchRetailerSettingsQuery,
} from '~/api/easy-estimator-api/retailer-settings-api/retailerSettingsService';
import { LOCAL_STORAGE_KEY } from '~/core/constants';
import { getSearchParameter } from '~/core/helpers';
import {
  ApplianceSectionType,
  RetailerFileSettings,
  RetailerSettings,
} from '~/core/models';
import { isDevMode } from '~/core/utils';

type useRetailerSettingsReturn = {
  retailerSettings: RetailerSettings | undefined;
  isOnlineRetailer: boolean;
  retailerFileSettings: RetailerFileSettings | undefined;
  isFetchingRetailerSettings: boolean;
  isFetchingRetailerFileSettings: boolean;
  getInStoreApplianceUrl: (applianceType: ApplianceSectionType) => string;
  // other results data from Settings API request
  retailerSettingsQueryResults: Omit<
    TypedUseQueryHookResult<
      RetailerSettings,
      RetailerSettingsParams,
      AxiosBaseQueryFunction
    >,
    'data' | 'isFetching'
  >;
  // other query result data from Settings File Request
  retailerFileSettingsQueryResults: Omit<
    TypedUseQueryHookResult<
      RetailerFileSettings,
      RetailerFileSettingsParams,
      AxiosBaseQueryFunction
    >,
    'data' | 'isFetching'
  >;
};

export const useRetailerSettings = (): useRetailerSettingsReturn => {
  const userToken = getSearchParameter(LOCAL_STORAGE_KEY.USER_TOKEN);
  // this one fetches the settings from the API
  const {
    isFetching: isFetchingRetailerSettings,
    data: retailerSettings,
    ...retailerSettingsQueryResults
  } = useFetchRetailerSettingsQuery({ userToken });

  const makeRetailFileUrl = (code: string) =>
    `/public/assets/retailers/${code}.settings.json`;

  // in dev mode, we use 'development' as the company code to load a local settings file
  // in production,we default to 'default' to load the default.settings.json file
  // which should always be present
  // TODO: figure out what we want to do with these settings files or if they can be eliminated entirely
  const companyCode = isDevMode() ? 'development' : 'default'; //retailerSettings?.companyCode;

  // this one is the local {companycode}.settings.json file
  const {
    isFetching: isFetchingRetailerFileSettings,
    data: retailerFileSettings,
    ...retailerFileSettingsQueryResults
  } = useFetchRetailerFileSettingsQuery(
    { url: makeRetailFileUrl(companyCode) },
    { skip: !companyCode }
  );

  const getInStoreApplianceUrl = useCallback(
    (applianceType: ApplianceSectionType): string =>
      retailerFileSettings?.inStoreApplianceURLs[applianceType] ?? '',
    [retailerFileSettings]
  );

  return {
    retailerSettings,
    isOnlineRetailer: retailerSettings?.isOnlineRetailer ?? true,
    retailerFileSettings,
    isFetchingRetailerSettings,
    isFetchingRetailerFileSettings,
    retailerSettingsQueryResults,
    retailerFileSettingsQueryResults,
    getInStoreApplianceUrl,
  };
};
